import { ComboBox, IComboBoxOption, IDropdownOption, MessageBarType, Toggle } from '@fluentui/react';
import {
  Button,
  buttonStylesGhost,
  buttonStylesPrimary,
  DatePicker,
  Dropdown,
  FileUpload,
  flattenNestedRows,
  hasChangedFieldValues,
  Loader,
  loaderStylesSpinnerXLarge,
  MessageBarList,
  messageBarListStylesToast,
  sortList,
  TextField,
  useClassNames,
  useMessageBarList,
  useToast,
} from '@h2oai/ui-kit';
import { useCallback, useEffect, useRef, useState } from 'react';

import { fsApi, useTransformationUpload } from '../../featurestore/api';
import {
  BackfillOptions,
  CoreService,
  DraftToFeatureSetRequest,
  Feature,
  FeatureSchemaDraft,
  FeatureSetVersionDescription,
  ListableFeatureSet,
  ListableFeatureSetResponse,
  ListFeatureSetVersionsResponse,
  ListFeaturesResponse,
  MajorVersionDraftToFeatureSetVersionRequest,
  MajorVersionDraftToFeatureSetVersionResponse,
  TransformationType,
} from '../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import {
  buttonStylesPrimaryPanel,
  DraftToFeatureSet,
  FeatureSchemaDraftWithDepth,
  FeatureSetAction,
} from '../../featurestore/models';
import {
  getErrorMessageBarMessage,
  getMd5Checksum,
  getSchemaErrorMessage,
  hasAPIError,
  unflattenSchema,
} from '../../featurestore/utils';
import { EditFeatureList } from '../EditFeatureList/EditFeatureList';
import { EditFeatureMapList } from '../EditFeatureMapList/EditFeatureMapList';
import { renderFieldLabelAndInfo } from '../SelectDataSource/SelectDataSource';
import {
  IRegisterFeatureSetAddSchemaClassNames,
  IRegisterFeatureSetAddSchemaStyles,
  registerFeatureSetAddSchemaStylesDefault,
  toggleStylesRegisterFeatureSetAddSchema,
} from './RegisterFeatureSetAddSchema.styles';

const updateSchema = (values: DraftToFeatureSet = {}, features: FeatureSchemaDraftWithDepth[]) => ({
  ...values,
  schema: unflattenSchema(features),
});

const getTimeTravelOptions = (featureNameSet: Set<string>): IComboBoxOption[] => {
  const options: IComboBoxOption[] = [];
  featureNameSet.forEach((name) => options.push({ key: name, text: name }));
  return options;
};

const getDraftToFeatureSetRequest = (values: DraftToFeatureSet): DraftToFeatureSetRequest => {
  const { projectId, name, description, schema, timeTravelColumn, tags } = values;
  return { projectId, name, description, schema, timeTravelColumn, tags };
};

const getMajorVersionDraftToFeatureSetVersionRequest = (
  values: DraftToFeatureSet
): MajorVersionDraftToFeatureSetVersionRequest => {
  const {
    projectId,
    schema,
    reason,
    backfillOptions,
    featureSetId,
    featureSetVersion,
    timeTravelColumn: {
      timeTravelColumnName: newTimeTravelColumn,
      timeTravelColumnFormat: newTimeTravelColumnFormat,
      isPartitionColumn: useTimeTravelColumnAsPartition,
    } = {},
  } = values;
  return {
    projectId,
    schema,
    reason,
    backfillOptions,
    useTimeTravelColumnAsPartition,
    featureSetId,
    featureSetVersion,
    newTimeTravelColumn,
    newTimeTravelColumnFormat,
  };
};

const getGetListableFeatureSetAPIsForVersions = (
  featureSetId?: string,
  versions: FeatureSetVersionDescription[] = []
) => {
  return versions.map(({ version }) =>
    fsApi(CoreService.GetListableFeatureSet, {
      featureSetId,
      featureSetVersion: version,
    })
  );
};

interface FeatureWithDepth extends Feature {
  depthWithHasNextSibling: boolean[];
}

const convertFeaturesToFeatureSchemaDraft = (
  items: FeatureWithDepth[],
  { partitionBy = [], primaryKey = [] }: ListableFeatureSet = {}
): FeatureSchemaDraft[] => {
  const primaryKeySet = new Set<string | undefined>(primaryKey);
  const partitionColumnSet = new Set<string | undefined>(partitionBy);
  return items.map(
    ({
      name,
      dataType,
      description,
      specialData,
      profile: { featureType } = {},
      monitoring: { anomalyDetection } = {},
      depthWithHasNextSibling,
    }) => ({
      name,
      dataType,
      specialData,
      featureType,
      description,
      anomalyDetection,
      isPrimaryKey: primaryKeySet.has(name),
      isPartitionColumn: partitionColumnSet.has(name),
      depthWithHasNextSibling,
    })
  );
};

interface RefMeta {
  schemaIsChanged: boolean;
  draftFeatureSet?: DraftToFeatureSet;
  isNewVersionDraft?: boolean;
}

const initRefMeta = (draftFeatureSet?: DraftToFeatureSet): RefMeta => ({
  schemaIsChanged: false,
  draftFeatureSet,
});

interface IRegisterFeatureSetAddSchemaProps {
  action?: FeatureSetAction;
  draftFeatureSet: DraftToFeatureSet;
  featureSetDraftId?: string;
  changedFeatureSet?: boolean;
  draftFeatures?: FeatureSchemaDraftWithDepth[];
  isNewVersionDraft?: boolean;
  featureSetId?: string;
  featureSetVersion?: string;
  latestFeatureSet?: ListableFeatureSet;
  onSave?: (id: string, version?: string, backfill?: boolean) => void;
  onMovePrevious?: (draftFeatureSet: DraftToFeatureSet, draftFeatures?: FeatureSchemaDraftWithDepth[]) => void;
  onCancel?: () => void;
}

export const RegisterFeatureSetAddSchema = ({
  action = FeatureSetAction.register,
  draftFeatureSet,
  featureSetDraftId,
  draftFeatures,
  changedFeatureSet,
  featureSetId,
  featureSetVersion,
  isNewVersionDraft,
  latestFeatureSet,
  onSave,
  onMovePrevious,
  onCancel,
}: IRegisterFeatureSetAddSchemaProps) => {
  const [values, setValues] = useState<DraftToFeatureSet>({
    ...draftFeatureSet,
  });
  const isNewVersionFeatureSet =
    action === FeatureSetAction.newVersion || (action === FeatureSetAction.draft && isNewVersionDraft);
  const [showSpecialData, setShowSpacialData] = useState(false);
  const [timeTravel, setTimeTravel] = useState(false);
  const [features, setFeatures] = useState<FeatureSchemaDraftWithDepth[]>([]);
  const [updatedFeatures, setUpdatedFeatures] = useState<FeatureSchemaDraftWithDepth[]>([]);
  const [prevDraftFeatures, setPrevDraftFeatures] = useState<FeatureSchemaDraftWithDepth[]>();
  const [timeTravelNameOptions, setTimeTravelNameOptions] = useState<IComboBoxOption[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  // new version
  const [fileInfo, setFileInfo] = useState<{
    transformationType?: TransformationType;
    md5Checksum?: string;
    file?: File;
  }>({});
  const [backfill, setBackfill] = useState(false);
  const [mapFeature, setMapFeature] = useState(false);
  const [versionOptions, setVersionOptions] = useState<IDropdownOption[]>();

  const refMeta = useRef<RefMeta>(initRefMeta());
  const { addToast } = useToast();
  const { messages, addMessage, removeMessage } = useMessageBarList();
  const { getFileName } = useTransformationUpload();

  // for backfill
  const loadVersions = useCallback(async () => {
    setLoadingMessage('loading the versions');
    try {
      // If request doesn't have a featureSetVerion, it will return the latest feature set.
      const { versions = [] }: ListFeatureSetVersionsResponse = await fsApi(CoreService.ListFeatureSetVersions, {
        featureSetId,
      });
      const results = await Promise.all(
        getGetListableFeatureSetAPIsForVersions(featureSetId, versions).map((p) => p.catch((e) => e))
      );
      const options: IDropdownOption[] = [];
      results.forEach((d: ListableFeatureSetResponse) => {
        if (hasAPIError(d)) {
          addMessage(`An error has occurred while loading Feature Set for versions`);
        } else {
          if (d.listableFeatureSet?.recordsCount && +d.listableFeatureSet?.recordsCount > 0) {
            const version = d.listableFeatureSet?.version;
            if (version) {
              options.push({ key: +version, text: version });
            }
          }
        }
      });
      setVersionOptions(sortList(options, 'key'));
    } catch (err) {
      const { messageBarType, message } = getErrorMessageBarMessage(
        err,
        'An error has occurred while loading versions: '
      );
      addMessage(message as string, undefined, undefined, messageBarType);
    }
    setLoadingMessage('');
  }, [featureSetId]);

  // for new version
  const loadFeatures = useCallback(async () => {
    setLoadingMessage('loading the features');
    try {
      // If request doesn't have a featureSetVerion, it will return the latest feature set.
      const { features = [] }: ListFeaturesResponse = await fsApi(CoreService.ListFeatures, {
        featureSetId,
        featureSetVersion,
        pageSize: 1000,
      });
      setFeatures(
        convertFeaturesToFeatureSchemaDraft(
          flattenNestedRows(features, 'nestedFeatures') as FeatureWithDepth[],
          latestFeatureSet
        )
      );
    } catch (err) {
      const { messageBarType, message } = getErrorMessageBarMessage(
        err,
        'An error has occurred while loading features: '
      );
      addMessage(message as string, undefined, undefined, messageBarType);
    }
    setLoadingMessage('');
  }, [featureSetId, featureSetVersion]);

  const createFeatureSet = useCallback(async (reqValues: DraftToFeatureSet) => {
    try {
      const { featureSetId } = await fsApi(CoreService.CreateFeatureSetFromDraft, {
        ...getDraftToFeatureSetRequest(reqValues),
        id: action === FeatureSetAction.draft ? featureSetDraftId : undefined,
      });
      addToast({
        messageBarType: MessageBarType.success,
        message: `Feature set '${draftFeatureSet.name}' has been created!`,
      });
      onSave && onSave(featureSetId);
    } catch (err) {
      const { messageBarType, message } = getErrorMessageBarMessage(
        err,
        'An error has occurred while creating the draft feature set: '
      );
      addMessage(message as string, undefined, undefined, messageBarType);
    }
  }, []);
  const createNewVersionFeatureSet = useCallback(
    async (reqValues: DraftToFeatureSet) => {
      setLoadingMessage(`creating a new feature set major version...`);
      try {
        let backfillOptions: BackfillOptions | undefined = reqValues.backfillOptions
          ? { ...reqValues.backfillOptions }
          : undefined;
        if (backfill) {
          const { file, md5Checksum, transformationType } = fileInfo;
          const filename = await getFileName(file, md5Checksum, transformationType);
          backfillOptions = { ...backfillOptions, sparkPipeline: { filename } };
        }
        const {
          featureSetId: newFeatureSetId = '',
          featureSetName,
          featureSetVersion: newVersion,
        }: MajorVersionDraftToFeatureSetVersionResponse = await fsApi(
          CoreService.CreateFeatureSetVersionFromMajorVersionDraft,
          {
            ...getMajorVersionDraftToFeatureSetVersionRequest(reqValues),
            featureSetId,
            featureSetVersion,
            backfillOptions,
            id: action === FeatureSetAction.draft ? featureSetDraftId : undefined,
          }
        );
        addToast({
          messageBarType: MessageBarType.success,
          message: `The new version ${newVersion} of feature set '${featureSetName}' has been created successfully!`,
        });
        setLoadingMessage('');
        onSave && onSave(newFeatureSetId, newVersion, backfill);
      } catch (err) {
        const { messageBarType, message } = getErrorMessageBarMessage(
          err,
          'An error has occurred while creating the feature set major version draft: '
        );
        addMessage(message as string, undefined, undefined, messageBarType);
        setLoadingMessage('');
      }
    },
    [fileInfo, backfill]
  );
  const onUpdateFeatures = useCallback(
    (features: FeatureSchemaDraftWithDepth[], featureNameSetForTimeTravel?: Set<string>) => {
      refMeta.current = { schemaIsChanged: true };
      setUpdatedFeatures(features);
      if (featureNameSetForTimeTravel) {
        setTimeTravelNameOptions(getTimeTravelOptions(featureNameSetForTimeTravel));
      }
    },
    []
  );
  const onSubmit = useCallback(() => {
    const message = getSchemaErrorMessage(updatedFeatures);
    setErrorMessage(message);
    if (!message) {
      refMeta.current = initRefMeta();
      if ((action === FeatureSetAction.register || action === FeatureSetAction.draft) && !isNewVersionDraft) {
        createFeatureSet(updateSchema(values, updatedFeatures));
      } else {
        createNewVersionFeatureSet(updateSchema(values, updatedFeatures));
      }
    }
  }, [values, updatedFeatures, fileInfo]);
  useEffect(() => {
    setValues({
      ...draftFeatureSet,
    });
    setTimeTravel(!!draftFeatureSet.timeTravelColumn);
    const backFill = !!draftFeatureSet?.backfillOptions;
    setBackfill(!!draftFeatureSet?.backfillOptions);
    setMapFeature(!!draftFeatureSet?.backfillOptions?.featureMapping);
    if (backFill && !versionOptions) {
      loadVersions();
    }
    if (!draftFeatures) {
      if (action === FeatureSetAction.newVersion) {
        // load features from the latest feature set version
        loadFeatures();
      } else {
        if (draftFeatureSet) {
          // load features from draftFeatureSet
          setFeatures(
            draftFeatureSet.schema && draftFeatureSet.schema.length > 0
              ? flattenNestedRows(draftFeatureSet.schema, 'nested')
              : []
          );
        }
      }
    } else {
      setPrevDraftFeatures(draftFeatures);
    }
    refMeta.current = initRefMeta(draftFeatureSet);
  }, [draftFeatureSet, draftFeatures]);
  const classNames = useClassNames<IRegisterFeatureSetAddSchemaStyles, IRegisterFeatureSetAddSchemaClassNames>(
    'RegisterFeatureSetAddSchema',
    registerFeatureSetAddSchemaStylesDefault
  );
  return (
    <div className={classNames.root} data-test="register-feature-set-add-schema">
      <div className={classNames.messageBar}>
        <MessageBarList
          styles={messageBarListStylesToast}
          messages={messages}
          autoClose
          groupByMessage
          onRemoveMessage={removeMessage}
        />
      </div>
      <div className={classNames.form}>
        <div className={classNames.fields}>
          <Toggle
            styles={toggleStylesRegisterFeatureSetAddSchema}
            id="showSpecialData"
            label="Show special data fields"
            inlineLabel
            onChange={(_e, value) => setShowSpacialData(!!value)}
          />
          <EditFeatureList
            features={features}
            draftFeatures={prevDraftFeatures}
            onUpdateFeatures={onUpdateFeatures}
            showSpecialData={showSpecialData}
          />
          <Toggle
            styles={toggleStylesRegisterFeatureSetAddSchema}
            key="timeTravel"
            id="timeTravel"
            label={
              updatedFeatures.length === 0
                ? renderFieldLabelAndInfo(
                    'Explicit Time Travel Column',
                    'Enabling explicit time travel column requires at least one feature to be defined'
                  )
                : 'Explicit Time Travel Column'
            }
            disabled={updatedFeatures.length === 0}
            inlineLabel
            checked={timeTravel}
            onChange={(_e, value) => {
              setTimeTravel(!!value);
              if (!value) {
                setValues({
                  ...values,
                  timeTravelColumn: { isPartitionColumn: values.timeTravelColumn?.isPartitionColumn },
                });
              }
            }}
          />
          {timeTravel && (
            <>
              <div className={classNames.subTitle}>Time Travel</div>
              <ComboBox
                key="timeTravelColumnName"
                onRenderLabel={() => (
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '4px' }}>
                    <div style={{ lineHeight: '24px' }}>Name </div>
                  </div>
                )}
                selectedKey={values.timeTravelColumn?.timeTravelColumnName}
                options={timeTravelNameOptions}
                required={timeTravel}
                allowFreeform
                autoComplete="on"
                onChange={(_e, option) => {
                  if (option) {
                    setValues({
                      ...values,
                      timeTravelColumn: { ...(values.timeTravelColumn || {}), timeTravelColumnName: option.text },
                    });
                  }
                }}
              />
              <TextField
                name="timeTravelColumnFormat"
                key="timeTravelColumnFormat"
                label="Format"
                value={values.timeTravelColumn?.timeTravelColumnFormat || ''}
                onChange={(_e, timeTravelColumnFormat) =>
                  setValues({
                    ...values,
                    timeTravelColumn: { ...(values.timeTravelColumn || {}), timeTravelColumnFormat },
                  })
                }
              />
            </>
          )}
          <Toggle
            styles={toggleStylesRegisterFeatureSetAddSchema}
            id="isPartitionColumn"
            key="isPartitionColumn"
            label="Use Time Travel Feature As Partition"
            inlineLabel
            disabled={updatedFeatures.length === 0}
            checked={values.timeTravelColumn?.isPartitionColumn}
            onChange={(_e, isPartitionColumn) =>
              setValues({
                ...values,
                timeTravelColumn: { ...(values.timeTravelColumn || {}), isPartitionColumn },
              })
            }
          />
          {isNewVersionFeatureSet && (
            <>
              <Toggle
                styles={toggleStylesRegisterFeatureSetAddSchema}
                id="backfill"
                label="Backfill"
                inlineLabel
                checked={backfill}
                onChange={(_e, backfill) => {
                  setBackfill(!!backfill);
                  setValues({
                    ...values,
                    backfillOptions: backfill ? {} : undefined,
                  });
                  if (!!backfill && !versionOptions) {
                    loadVersions();
                  }
                }}
              />
              {backfill && (
                <>
                  <div className={classNames.subTitle}>Select version from which to back-fill data from</div>
                  <Dropdown
                    data-test="dropdown-version"
                    styles={{ root: { width: '150px' } }}
                    placeholder="Select a version"
                    options={versionOptions || []}
                    selectedKey={values.backfillOptions?.fromVersion}
                    onChange={(_e, option) => {
                      if (option) {
                        setValues({
                          ...values,
                          backfillOptions: { ...values.backfillOptions, fromVersion: option.key as string },
                        });
                      }
                    }}
                  />
                  <DatePicker
                    label="From date"
                    value={values.backfillOptions?.fromDate}
                    onSelectDate={(fromDate) => {
                      if (fromDate) {
                        setValues({ ...values, backfillOptions: { ...values.backfillOptions, fromDate } });
                      }
                    }}
                  />
                  <DatePicker
                    label="To date"
                    value={values.backfillOptions?.toDate}
                    onSelectDate={(toDate) => {
                      if (toDate) {
                        setValues({ ...values, backfillOptions: { ...values.backfillOptions, toDate } });
                      }
                    }}
                  />
                  <div className={classNames.fileUploadContainer}>
                    <div className={classNames.fileUploadContainerHeader}>
                      Select the spark transformation pipeline file(zip)
                    </div>
                    <FileUpload
                      uploadCallback={(files: File[]) => {
                        if (files[0]) {
                          const reader = new FileReader();
                          reader.onloadend = (e) => {
                            const md5Checksum = getMd5Checksum(e);
                            if (md5Checksum) {
                              setFileInfo({
                                ...fileInfo,
                                md5Checksum,
                                transformationType: TransformationType.TransformationSparkPipeline,
                                file: files[0],
                              });
                            }
                          };
                          reader.readAsArrayBuffer(files[0]);
                        }
                      }}
                      maxFileSize={2e8}
                      maxSize={2e8}
                      fileExtensions={['zip']}
                      data-test="upload-spark-transformation-file-upload"
                    />
                  </div>
                  <Toggle
                    styles={toggleStylesRegisterFeatureSetAddSchema}
                    id="featureMapping"
                    label="Override default values for missing features"
                    inlineLabel
                    checked={mapFeature}
                    onChange={(_e, mapFeature) => {
                      setMapFeature(!!mapFeature);
                      if (!mapFeature) {
                        setValues({
                          ...values,
                          backfillOptions: { ...values.backfillOptions, featureMapping: undefined },
                        });
                      }
                      if (refMeta.current.draftFeatureSet?.backfillOptions) {
                        refMeta.current.draftFeatureSet.backfillOptions.featureMapping = undefined;
                      }
                    }}
                  />
                  {mapFeature && (
                    <EditFeatureMapList
                      map={refMeta.current.draftFeatureSet?.backfillOptions?.featureMapping}
                      features={updatedFeatures}
                      onUpdate={(featureMapping) => {
                        if (refMeta.current.draftFeatureSet?.backfillOptions) {
                          refMeta.current.draftFeatureSet.backfillOptions.featureMapping = undefined;
                        }
                        setValues({ ...values, backfillOptions: { ...values.backfillOptions, featureMapping } });
                      }}
                    />
                  )}
                </>
              )}
            </>
          )}
        </div>
        <div className={classNames.buttons}>
          <div className={classNames.errorMessage}>{errorMessage ? <div>{errorMessage}</div> : null}</div>
          <div>
            <Button styles={buttonStylesGhost} text="Cancel" onClick={onCancel} style={{ marginRight: 8 }} />
            <Button
              styles={[buttonStylesPrimary, buttonStylesPrimaryPanel]}
              style={{ marginRight: 8 }}
              text="Previous"
              onClick={() => onMovePrevious && onMovePrevious({ ...values }, updatedFeatures)}
            />
            <Button
              styles={[buttonStylesPrimary, buttonStylesPrimaryPanel]}
              text="Save"
              disabled={
                (timeTravel && !values.timeTravelColumn?.timeTravelColumnName) ||
                (backfill && !values.backfillOptions?.fromVersion) ||
                (loadingMessage || !values.id
                  ? !values.name
                  : !hasChangedFieldValues(draftFeatureSet, values) &&
                    !refMeta.current.schemaIsChanged &&
                    !changedFeatureSet)
              }
              onClick={onSubmit}
            />
          </div>
        </div>
      </div>
      {loadingMessage && (
        <Loader styles={[loaderStylesSpinnerXLarge, { root: { height: '100%' } }]} label={loadingMessage} />
      )}
    </div>
  );
};
